<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  nameFechaInicio: { type: String, default: null },
  nameFechaFin: { type: String, default: null },
  fechaInicio: { type: String, default: null },
  fechaFin: { type: String, default: null },
});

const locale =
  (typeof document !== 'undefined' && document.documentElement.lang) || 'es';

const MESES = Array.from({ length: 12 }, (_, i) => ({
  valor: i + 1,
  nombre: new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2000, i, 1)),
}));

function diasParaMes(mes) {
  if (!mes) return Array.from({ length: 31 }, (_, i) => i + 1);
  return Array.from({ length: new Date(2000, mes, 0).getDate() }, (_, i) => i + 1);
}

function parsearFechaISO(isoDate) {
  if (!isoDate) return { mes: null, dia: null };
  const partes = isoDate.split('-').map(Number);
  if (partes.length < 3 || partes.some(isNaN)) return { mes: null, dia: null };
  return { mes: partes[1], dia: partes[2] };
}

const inicioParseado = parsearFechaISO(props.fechaInicio);
const finParseado = parsearFechaISO(props.fechaFin);

const mesInicio = ref(inicioParseado.mes);
const diaInicio = ref(inicioParseado.dia);
const mesFin = ref(finParseado.mes);
const diaFin = ref(finParseado.dia);

const diasInicio = computed(() => diasParaMes(mesInicio.value));
const diasFin = computed(() => diasParaMes(mesFin.value));

watch(mesInicio, (nuevoMes) => {
  if (!nuevoMes) return;
  const maxDia = new Date(2000, nuevoMes, 0).getDate();
  if (diaInicio.value && diaInicio.value > maxDia) {
    diaInicio.value = null;
  }
});

watch(mesFin, (nuevoMes) => {
  if (!nuevoMes) return;
  const maxDia = new Date(2000, nuevoMes, 0).getDate();
  if (diaFin.value && diaFin.value > maxDia) {
    diaFin.value = null;
  }
});

const fechaInicio = computed(() => {
  if (!mesInicio.value || !diaInicio.value) return '';
  const mes = String(mesInicio.value).padStart(2, '0');
  const dia = String(diaInicio.value).padStart(2, '0');
  return `2000-${mes}-${dia}`;
});

const fechaFin = computed(() => {
  if (!mesFin.value || !diaFin.value) return '';
  const mes = String(mesFin.value).padStart(2, '0');
  const dia = String(diaFin.value).padStart(2, '0');
  return `2000-${mes}-${dia}`;
});
</script>

<template>
  <div class="rango-dia-mes">
    <div class="input-group">
      <div class="rango-dia-mes__selector">
        <select v-model="diaInicio" class="form-control rango-dia-mes__dia">
          <option :value="null">{{ $t('Dia') }}</option>
          <option v-for="dia in diasInicio" :key="dia" :value="dia">
            {{ dia }}
          </option>
        </select>
        <select v-model="mesInicio" class="form-control rango-dia-mes__mes">
          <option :value="null">{{ $t('Mes') }}</option>
          <option v-for="mes in MESES" :key="mes.valor" :value="mes.valor">
            {{ mes.nombre }}
          </option>
        </select>
        <input v-if="nameFechaInicio" type="hidden" :name="nameFechaInicio" :value="fechaInicio" />
      </div>

      <span><i class="far fa-arrows-h"></i></span>

      <div class="rango-dia-mes__selector">
        <select v-model="diaFin" class="form-control rango-dia-mes__dia">
          <option :value="null">{{ $t('Dia') }}</option>
          <option v-for="dia in diasFin" :key="dia" :value="dia">
            {{ dia }}
          </option>
        </select>
        <select v-model="mesFin" class="form-control rango-dia-mes__mes">
          <option :value="null">{{ $t('Mes') }}</option>
          <option v-for="mes in MESES" :key="mes.valor" :value="mes.valor">
            {{ mes.nombre }}
          </option>
        </select>
        <input v-if="nameFechaFin" type="hidden" :name="nameFechaFin" :value="fechaFin" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rango-dia-mes {
  .input-group {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__selector {
    display: flex;
    gap: 4px;
    flex: 1;
  }

  &__mes {
    flex: 2;
  }

  &__dia {
    flex: 1;
  }
}
</style>
